import React, { useEffect, useRef, useState } from "react";
import { checkUsernameAvailable } from "../api/api";
import { usernameFormatError } from "../lib/accountCredentials.js";
import { isUserInputEvent } from "../lib/blockBrowserAutofill";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { cn } from "../lib/utils";

const DEBOUNCE_MS = 350;

/**
 * Username entry with live uniqueness checking.
 * @param {Object} props
 * @param {string} [props.id]
 * @param {string} [props.label]
 * @param {string} props.value
 * @param {function(string): void} props.onChange
 * @param {string} [props.currentUsername] Existing username to treat as available
 * @param {number} [props.excludeId] Account id to ignore in the availability check
 * @param {string} [props.excludeType] Account type for the exclude id
 * @param {boolean} [props.required]
 * @param {string} [props.autoComplete]
 * @param {string} [props.inputClassName]
 * @param {string} [props.className]
 * @param {function(boolean): void} [props.onValidityChange]
 * @param {function(): void} [props.onUserEdit] Called only when the person types in the field
 * @returns {JSX.Element}
 */
export default function UsernameField({
  id = "username",
  label = "Username",
  value,
  onChange,
  currentUsername = "",
  excludeId,
  excludeType,
  required = true,
  autoComplete = "username",
  inputClassName,
  className,
  onValidityChange,
  onUserEdit,
}) {
  const [check, setCheck] = useState({ username: "", available: false, message: "" });
  const requestIdRef = useRef(0);
  const trimmed = (value || "").trim();
  const ownName = (currentUsername || "").trim();
  const isOwn = Boolean(ownName) && trimmed.toLowerCase() === ownName.toLowerCase();
  const formatError = isOwn ? "" : usernameFormatError(value);
  const needsCheck = Boolean(trimmed) && !isOwn && !formatError;
  const checkSettled = check.username === trimmed;
  const checking = needsCheck && !checkSettled;
  const takenMessage = needsCheck && checkSettled && !check.available ? check.message : "";
  const valid = isOwn
    ? Boolean(trimmed)
    : Boolean(trimmed) && !formatError && checkSettled && check.available;

  useEffect(() => {
    onValidityChange?.(valid);
  }, [valid, onValidityChange]);

  useEffect(() => {
    if (!trimmed || isOwn || formatError) {
      requestIdRef.current += 1;
      return undefined;
    }

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    const timer = window.setTimeout(async () => {
      const result = await checkUsernameAvailable(trimmed, excludeId, excludeType);
      if (requestId !== requestIdRef.current) return;
      if (result.error) {
        setCheck({
          username: trimmed,
          available: false,
          message: "Could not check username. Try again.",
        });
        return;
      }
      setCheck({
        username: trimmed,
        available: Boolean(result.available),
        message: result.available ? "" : result.reason || "This username is taken",
      });
    }, DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timer);
    };
  }, [trimmed, isOwn, formatError, excludeId, excludeType]);

  const showFormatError = Boolean(trimmed) && Boolean(formatError);
  const errorMessage = showFormatError ? formatError : takenMessage;

  return (
    <div className={cn("space-y-2", className)}>
      {label ? (
        <Label htmlFor={id}>
          {label}
          {required ? <span className="ml-1 text-error">*</span> : null}
        </Label>
      ) : null}
      <Input
        id={id}
        name="username"
        autoComplete={autoComplete}
        required={required}
        value={value}
        onChange={(event) => {
          onChange(event.target.value);
          if (isUserInputEvent(event)) onUserEdit?.();
        }}
        aria-invalid={Boolean(errorMessage)}
        aria-describedby={errorMessage ? `${id}-error` : undefined}
        className={cn(errorMessage && "border-error", inputClassName)}
      />
      {checking ? (
        <p className="text-sm text-text-secondary" role="status">
          Checking username…
        </p>
      ) : null}
      {errorMessage ? (
        <p id={`${id}-error`} className="text-sm text-error" role="alert">
          {errorMessage}
        </p>
      ) : null}
    </div>
  );
}
